import fetchJsonp from 'fetch-jsonp';
import * as queryString from 'querystring';

import { BannersDataProvider } from '../../api/banners/BannersDataProvider';
import { Lang } from '../../typings/common';
import { RegionSuggestElem } from '../../typings/regions';

type GeocodeResult = ymaps.GeocodeResult;
type YmapsResponseGeocode = ymaps.YmapsResponseGeocode;
type IMapConstructorState = ymaps.IMapConstructorState;
type IMapConstructorOptions = ymaps.IMapConstructorOptions;
type YMap = ymaps.Map;

export const MAP_API_KEY = '87efd73e-b616-455f-997f-146aabcb37c3';
export const MAP_API_URL = `https://api-maps.yandex.ru/2.1/?apikey=${MAP_API_KEY}&lang=ru_RU`;
export const MAP_API_SUGGEST_URL = `https://suggest-maps.yandex.ru/suggest-geo`;

export type SuggestItem = { text: string };
export type Point = [number, number];

export enum MapApiSuggestRequestSearchType {
    all = 'all',
    tp = 'tp',
    addr = 'addr',
    org = 'org',
    taxi = 'taxi',
    index = 'index',
    tune = 'tune'
}

export interface MapApiSuggestRequestParams {
    part: string;
    search_type: MapApiSuggestRequestSearchType;
    v: number;
    ll?: string;
    spn?: string;
    n?: number;
    bases?: string;
    lang?: Lang;
}

export class YandexMapHelper {
    private static loadPromise?: Promise<void>;

    private static bannersDataProvider: BannersDataProvider;

    static setApiManager(bannersDataProvider: BannersDataProvider): void {
        this.bannersDataProvider = bannersDataProvider;
    }

    static loadScriptMap(): Promise<Event> {
        return new Promise(
            (resolve, reject): void => {
                const script = document.createElement('script');
                script.onload = resolve;
                script.onerror = reject;
                script.src = MAP_API_URL;

                document.head.appendChild(script);
            }
        );
    }

    static initApiMap(): Promise<void> {
        YandexMapHelper.loadPromise =
            YandexMapHelper.loadPromise ||
            new Promise(
                (resolve): Promise<void> =>
                    YandexMapHelper.loadScriptMap().then(() =>
                        ymaps.ready(resolve)
                    )
            );
        return YandexMapHelper.loadPromise;
    }

    static getGeoObject(
        address: string | Point
    ): Promise<{ geoObject: GeocodeResult; geoId: number }> {
        return YandexMapHelper.getGeoObjects(address, 1)
            .then((geoObjects: GeocodeResult[]) => {
                return geoObjects[0];
            })
            .then(
                (geoObject: GeocodeResult): GeocodeResult => {
                    if (typeof address === 'string') {
                        return YandexMapHelper.bannersDataProvider
                            .getGeoByAddress(address)
                            .then(data => {
                                return {
                                    geoObject,
                                    geoId: data ? data.geoId : undefined
                                };
                            });
                    } else {
                        const [y, x] = address;

                        return YandexMapHelper.bannersDataProvider
                            .getByCoordinates(x, y)
                            .then(data => {
                                return {
                                    geoObject,
                                    geoId: data ? data.geoId : undefined
                                };
                            });
                    }
                }
            );
    }

    static createMap(
        parentElement: string | HTMLElement,
        state: Partial<IMapConstructorState>,
        options?: Partial<IMapConstructorOptions>
    ): YMap {
        return new ymaps.Map(parentElement, state, options);
    }

    static getMapApiSuggestItems(
        params: MapApiSuggestRequestParams
    ): Promise<SuggestItem[] | RegionSuggestElem[]> {
        let requestUrl = MAP_API_SUGGEST_URL;
        const paramsString = queryString.stringify(params);

        if (paramsString) {
            requestUrl += '?' + paramsString;
        }

        return fetchJsonp(requestUrl).then(data => {
            return data.ok ? data.json().then(_data => _data.results) : [];
        });
    }

    static createGeoObjectFromPoint(point: number[]): ymaps.GeoObject {
        return new ymaps.GeoObject({
            // Описание геометрии.
            geometry: {
                type: 'Point',
                coordinates: point
            }
        });
    }

    static getZoomBounds = (
        bounds: number[][],
        changeCoefficient: number
    ): number[][] => {
        const resultBounds: number[][] = [];
        resultBounds.push(
            bounds[0].map(item => {
                return item - changeCoefficient;
            })
        );
        resultBounds.push(
            bounds[1].map(item => {
                return item + changeCoefficient;
            })
        );
        return resultBounds;
    };

    static getGeolocationFromPoint(): ymaps.vow.Promise<number[] | null> {
        return ymaps.geolocation
            .get({
                provider: 'yandex'
            })
            .then((result: GeocodeResult) => {
                const geoObject = result.geoObjects.get(0);
                return geoObject.geometry.getCoordinates();
            });
    }

    static getMetroStations(point: number[]): Promise<void> {
        return ymaps.geocode(point, {
            kind: 'metro',
            results: 1
        });
    }

    private static getGeoObjects(
        address: string | number[],
        results: number
    ): Promise<GeocodeResult[]> {
        return ymaps
            .geocode(address, { results })
            .then((res: YmapsResponseGeocode) => {
                return res.geoObjects.toArray();
            });
    }
}
